<!-- MvrSummary.vue -->
<script setup lang="ts">
import { computed, defineProps, ref } from 'vue'
import { getChargeableChipColor, getCaseStatusDisplayText, getGenderText } from '@/utils/DriverUtils'
import { DataTableHeader } from '@/models/DataTableHeader'
import { OrderMvrResponseBody, Violation, Accident, Action } from '@/models/DriverInformationModels'
import { useDisplayFormatters } from '@/composables/UseDisplayFormatters';

const props = defineProps<{
  mvrData?: OrderMvrResponseBody;
}>()

// Use the display formatters composable
const { formatDate } = useDisplayFormatters()

// Use the provided MVR data
const summaryData = computed(() => props.mvrData)

const driverName = computed(() => {
  const driverInfo = props.mvrData?.driverInfo
  if (!driverInfo) return 'Unknown Driver'
  
  const { firstName, middleInitial, lastName } = driverInfo
  const middle = middleInitial ? `${middleInitial}. ` : ''
  return `${firstName || ''} ${middle}${lastName || ''}`.trim()
})

const totalRecords = computed(() => {
  const violations = summaryData.value?.mvrActivity?.violations?.length || 0
  const accidents = summaryData.value?.mvrActivity?.accidents?.length || 0
  const actions = summaryData.value?.mvrActivity?.actions?.length || 0
  return violations + accidents + actions
})

const isViolation = (record: Violation | Accident | Action): record is Violation => {
  return record.recordType === 'Violation' || 'dmvPoints' in record
}

// Common mapping function for all record types
const mapRecordToTableItem = (record: Violation | Accident | Action, recordType: string) => {
  const points = isViolation(record) ? 
    (record.dmvPoints?.includes('/') ? 
     record.dmvPoints?.split('/')[0] : 
     record.dmvPoints || '0') : 
    0;
  
  return {
    date: record.primaryDate || '',
    recordType: record.recordType || recordType,
    fullSVC: record.standardViolation?.fullSVC || '',
    svcDescription: record.standardViolation?.description || record.nationalText || '',
    points: points,
    caseStatusIndicator: record.caseStatusIndicator || '',
    chargeable: getCaseStatusDisplayText(record.caseStatusIndicator || ''),
    convictionDate: record.normalizedConvictionDate || ''
  }
}

// Map all records using the interfaces directly - no need for separate MappedRecord interface
const allRecords = computed(() => {
  // Map violations using standardViolation properties
  const violations = summaryData.value?.mvrActivity?.violations?.map(violation => 
    mapRecordToTableItem(violation, 'Violation')
  ) || []
  
  // Map accidents using standardViolation properties  
  const accidents = summaryData.value?.mvrActivity?.accidents?.map(accident => 
    mapRecordToTableItem(accident, 'Accident')
  ) || []
  
  // Map actions using standardViolation properties
  const actions = summaryData.value?.mvrActivity?.actions?.map(action => 
    mapRecordToTableItem(action, 'Action')
  ) || []
  
  // Combine all records
  return [...violations, ...accidents, ...actions]
})

const headers = ref<DataTableHeader[]>([
  { title: 'Date', key: 'date', align: 'start' },
  { title: 'Record Type', key: 'recordType', align: 'start' },
  { title: 'SVC', key: 'fullSVC', align: 'start' },
  { title: 'SVC Description', key: 'svcDescription', align: 'start' },
  { title: 'Points', key: 'points', align: 'start' },
  { title: 'Chargeable', key: 'chargeable', align: 'start' },
  { title: 'Conviction Date', key: 'convictionDate', align: 'start' },
]);
</script>

<template>
  <v-container class="mvr-summary pa-4" aria-label="MVR Summary">
    <div class="text-h6 mb-2 text-start pa-0">MVR Summary Details</div>

    <!-- Driver + License Info -->
    <v-row>
      <v-col cols="12" sm="6">
        <div class="text-start">
          <p class="mb-1">
            <strong>Name:</strong> {{ driverName }}
          </p>
          <p class="text-caption text-medium-emphasis mb-0">
            DOB: {{ summaryData?.driverInfo?.dob ? formatDate(summaryData.driverInfo.dob, 'MM/DD/YYYY') : '—'  }} | Gender: {{ getGenderText(summaryData?.driverInfo?.gender) }}
          </p>
        </div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="text-start">
          <p class="mb-1">
            <strong>DL#:</strong> {{ summaryData?.driverInfo?.licenseNumber }} ({{ summaryData?.driverInfo?.licenseState }})
          </p>
          <p class="text-caption text-medium-emphasis mb-0">
            Class: {{ summaryData?.driverInfo?.dlClass || '—' }} | Status: 
            <v-chip :color="summaryData?.licenceStatus?.toUpperCase() === 'VALID' ? 'success' : 'error'" class="ml-1 text-white" size="small" label>
              {{ summaryData?.licenceStatus?.toUpperCase() || 'UNKNOWN' }}
            </v-chip>
          </p>
        </div>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <!-- MVR Status Info -->
    <v-row>
      <v-col cols="12" sm="6">
        <div class="text-start">
          <p class="mb-1"><strong>Order Id:</strong> {{ summaryData?.orderId || '—' }}</p>
          <p class="mb-0"><strong>Status:</strong> {{ summaryData?.status || '—' }}</p>
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <div class="text-start">
          <p class="mb-1"><strong>Ordered:</strong> {{ summaryData?.dateTimeOrdered ? formatDate(summaryData.dateTimeOrdered, 'MM/DD/YYYY') : '—' }}</p>
          <p class="mb-0"><strong>Completed:</strong> {{ summaryData?.dateTimeCompleted ? formatDate(summaryData.dateTimeCompleted, 'MM/DD/YYYY') : '—' }}</p>
        </div>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <!-- Records -->
    <div class="px-0 pt-2">
      <div class="text-subtitle-1 mb-2 text-start">
        Records ({{ totalRecords }})
      </div>

      <v-data-table 
        :headers="headers" 
        :items="allRecords" 
        class="text-left" 
        density="compact"
        hover 
        color="primaryMoe"
        :items-per-page="10"
        :sort-by="[{key: 'date', order: 'asc'}]"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 25, -1],
          itemsPerPageText: 'Records per page:',
          showFirstLastPage: true
        }"
      >
        
        <!-- eslint-disable vue/valid-v-slot -->
        <template #item.date="{ item }">
          {{ item.date ? formatDate(item.date,'MM/DD/YYYY') : '—' }}
        </template>

        <!-- eslint-disable vue/valid-v-slot -->
        <template #item.chargeable="{ item }">
          <v-chip :color="getChargeableChipColor(item.caseStatusIndicator || '')" size="small" label class="text-white">
            {{ item.chargeable }}
          </v-chip>
        </template>

        <!-- eslint-disable vue/valid-v-slot -->
        <template #item.convictionDate="{ item }">
          {{ item.convictionDate ? formatDate(item.convictionDate, 'MM/DD/YYYY') : '—' }}
        </template>

        <!-- No data slot for empty state -->
        <template #no-data>
          <div class="text-center text-medium-emphasis py-4">
            No records found.
          </div>
        </template>

      </v-data-table>
    </div>

    <v-card v-if="summaryData?.alertMessages?.code === 'SF'" title="Security Freeze" class="sc text-left mt-6">
      <v-card-text>
        {{ summaryData?.alertMessages?.message }}
      </v-card-text>
    </v-card>

  </v-container>
</template>

<style scoped>
</style>